import { useState } from "react";
import { Activity, RefreshCw, Play, ChevronDown, ChevronRight } from "lucide-react";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { api, apiError } from "../lib/api";

const ACTION_LABEL = {
  would_fire: { label: "Pronto", class: "bg-green-100 text-green-800 border-green-300" },
  skip_no_chat: { label: "No chat_id", class: "bg-red-100 text-red-800 border-red-300" },
  skip_out_of_window: { label: "Fuori orario", class: "bg-amber-100 text-amber-800 border-amber-300" },
  skip_non_production: { label: "Non-prod", class: "bg-slate-100 text-slate-700 border-slate-300" },
  skip_not_pending: { label: "Idle", class: "bg-slate-50 text-slate-500 border-slate-200" },
};

function actionBadge(action) {
  if (!action) return null;
  if (action.startsWith("waiting_silence")) {
    const m = action.match(/\((\d+)s\)/);
    const sec = m ? parseInt(m[1], 10) : 0;
    const min = Math.ceil(sec / 60);
    return { label: `Attendo ${min}min`, class: "bg-blue-50 text-blue-800 border-blue-300" };
  }
  return ACTION_LABEL[action] || { label: action, class: "bg-slate-100 text-slate-700 border-slate-300" };
}

function fmt(dt) {
  if (!dt) return "—";
  try {
    return new Date(dt).toLocaleString("it-IT", {
      day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return dt;
  }
}

export default function BatchAckDiagnostic() {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/telegram/batch-ack/diagnose");
      setData(data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };

  const runNow = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/admin/telegram/batch-ack/run");
      const fired = data?.fired || 0;
      const cleared = data?.cleared || 0;
      const errs = data?.errors || 0;
      if (fired > 0) {
        toast.success(`ACK inviati: ${fired}`);
      } else if (cleared > 0) {
        toast.info(`Nessun nuovo contenuto. Stati ripuliti: ${cleared}`);
      } else if (data?.skipped_reason) {
        toast.warning(`Saltato: ${data.skipped_reason}`);
      } else {
        toast.info("Nessun cliente da notificare ora");
      }
      if (errs > 0) toast.error(`Errori: ${errs}`);
      // Reload diagnose after run
      await load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setRunning(false);
    }
  };

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && !data) await load();
  };

  return (
    <div
      data-testid="settings-batch-ack-card"
      className="rounded-3xl border border-[var(--mp-ink)] bg-white shadow-brutal-sm p-6 sm:p-8 space-y-4"
    >
      <button
        type="button"
        onClick={toggle}
        data-testid="batch-ack-toggle"
        className="w-full flex items-center justify-between gap-3 text-left"
      >
        <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[var(--mp-muted)] flex items-center gap-2">
          <Activity className="h-3.5 w-3.5" /> Diagnostica ACK Telegram
        </div>
        {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
      </button>

      {!open && (
        <div className="text-sm text-[var(--mp-muted)] leading-relaxed">
          Stato del cron "Ricevuto ✓ Contenuti in lavorazione" per ogni cliente. Click per espandere.
        </div>
      )}

      {open && (
        <>
          <div className="text-sm text-[var(--mp-muted)] leading-relaxed">
            Mostra lo stato batch_ack per ogni cliente. <strong>Esegui ora</strong> forza una run del cron
            (ignora finestra 7-22 e guard production), utile se il bot smette di rispondere.
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <Button
              variant="outline"
              size="sm"
              onClick={load}
              disabled={loading}
              data-testid="batch-ack-refresh"
              className="rounded-full border-[var(--mp-ink)] h-8 text-[11px]"
            >
              <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} />
              {loading ? "Carico…" : "Aggiorna"}
            </Button>
            <Button
              size="sm"
              onClick={runNow}
              disabled={running}
              data-testid="batch-ack-run"
              className="rounded-full bg-[var(--mp-ink)] hover:bg-black text-white h-8 text-[11px]"
            >
              <Play className={`h-3.5 w-3.5 mr-1.5 ${running ? "animate-pulse" : ""}`} />
              {running ? "Esecuzione…" : "Esegui ora"}
            </Button>
          </div>

          {data && (
            <div className="grid grid-cols-2 gap-2 text-[11px] bg-[var(--mp-dash-bg)] rounded-xl p-3">
              <div><span className="text-[var(--mp-muted)]">Ora Roma:</span> {fmt(data.now_rome)}</div>
              <div>
                <span className="text-[var(--mp-muted)]">Pod:</span>{" "}
                <span className={data.is_production_pod ? "text-green-700 font-bold" : "text-amber-700 font-bold"}>
                  {data.is_production_pod ? "Production" : "Preview"}
                </span>
              </div>
              <div>
                <span className="text-[var(--mp-muted)]">Finestra:</span>{" "}
                <span className={data.window?.in_window_now ? "text-green-700" : "text-red-700"}>
                  {data.window?.start_hour}-{data.window?.end_hour_exclusive} ({data.window?.in_window_now ? "aperta" : "chiusa"})
                </span>
              </div>
              <div><span className="text-[var(--mp-muted)]">Ultimo webhook:</span> {fmt(data.last_webhook_at)}</div>
              <div className="col-span-2">
                <span className="text-[var(--mp-muted)]">Clienti monitorati:</span> {data.clients_count}
              </div>
            </div>
          )}

          {data?.clients?.length > 0 && (
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {data.clients.map((c) => {
                const badge = actionBadge(c.predicted_action);
                return (
                  <div
                    key={c.client_id}
                    data-testid={`batch-ack-client-${c.client_id}`}
                    className="rounded-xl border border-[var(--mp-border)] bg-white p-3 text-[11px] space-y-1"
                  >
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <div className="font-bold text-[12px]">{c.name_salone || c.email}</div>
                      {badge && (
                        <span className={`px-2 py-0.5 rounded-full border text-[10px] font-bold ${badge.class}`}>
                          {badge.label}
                        </span>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-1 text-[var(--mp-muted)]">
                      <div>Pending: <span className={c.batch_ack?.pending ? "text-blue-700 font-bold" : ""}>{c.batch_ack?.pending ? "sì" : "no"}</span></div>
                      <div>Chat ID: {c.batch_ack?.last_chat_id ?? c.telegram_chat_id ?? "—"}</div>
                      <div>Ultimo upload: {fmt(c.batch_ack?.last_attachment_at)}</div>
                      <div>Ultimo ACK: {fmt(c.batch_ack?.last_ack_at)}</div>
                      {c.batch_ack?.last_ack_count != null && (
                        <div>Ultimo count: {c.batch_ack.last_ack_count}</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {data && data.clients?.length === 0 && (
            <div className="text-sm text-[var(--mp-muted)] italic text-center py-4">
              Nessun cliente con stato batch_ack salvato.
            </div>
          )}
        </>
      )}
    </div>
  );
}
